// 通用工具函数

/**
 * 格式化时间显示
 * @param seconds 秒数
 * @returns 格式化的时间字符串
 */
export function formatTime(seconds: number): string {
  const hours = Math.floor(seconds / 3600)
  const minutes = Math.floor((seconds % 3600) / 60)
  const secs = Math.floor(seconds % 60)
  const milliseconds = Math.floor((seconds % 1) * 1000)
  
  return `${hours.toString().padStart(2, '0')}:${minutes.toString().padStart(2, '0')}:${secs.toString().padStart(2, '0')},${milliseconds.toString().padStart(3, '0')}`
}

/**
 * 防抖函数
 * @param func 要防抖的函数
 * @param wait 等待时间（毫秒）
 * @returns 防抖后的函数
 */
export function debounce<T extends (...args: any[]) => any>(
  func: T,
  wait: number
): (...args: Parameters<T>) => void {
  let timeout: NodeJS.Timeout | null = null
  
  return function executedFunction(...args: Parameters<T>) {
    const later = () => {
      timeout = null
      func(...args)
    }
    
    if (timeout) {
      clearTimeout(timeout)
    }
    timeout = setTimeout(later, wait)
  }
}

/**
 * 节流函数
 * @param func 要节流的函数
 * @param limit 限制时间（毫秒）
 * @returns 节流后的函数
 */
export function throttle<T extends (...args: any[]) => any>(
  func: T,
  limit: number
): (...args: Parameters<T>) => void {
  let inThrottle: boolean = false
  
  return function executedFunction(...args: Parameters<T>) {
    if (!inThrottle) {
      func(...args)
      inThrottle = true
      setTimeout(() => inThrottle = false, limit)
    }
  }
}

/**
 * 深拷贝对象
 * @param obj 要拷贝的对象
 * @returns 拷贝后的对象
 */
export function deepClone<T>(obj: T): T {
  if (obj === null || typeof obj !== 'object') {
    return obj
  }
  
  if (obj instanceof Date) {
    return new Date(obj.getTime()) as unknown as T
  }
  
  if (obj instanceof Array) {
    return obj.map(item => deepClone(item)) as unknown as T
  }
  
  if (typeof obj === 'object') {
    const clonedObj = {} as T
    for (const key in obj) {
      if (obj.hasOwnProperty(key)) {
        clonedObj[key] = deepClone(obj[key])
      }
    }
    return clonedObj
  }
  
  return obj
}

/**
 * 生成唯一ID
 * @returns 唯一ID字符串
 */
export function generateId(): string {
  return Date.now().toString(36) + Math.random().toString(36).substr(2)
}

/**
 * 检查是否为移动设备（纯 UA 正则）
 * @returns 是否为移动设备
 */
export function isMobile(): boolean {
  return /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(navigator.userAgent)
}

/**
 * 统一的"是否移动/触屏终端"判断，供路由分流与组件内部布局共用，避免各处标准不一致。
 *
 * 判断优先级：
 * 1. `navigator.userAgentData.mobile`（Client Hints）：Chromium 内核浏览器原生提供，
 *    由浏览器直接给出设备形态，不依赖 UA 字符串正则、不受各厂商 User-Agent Reduction 影响。
 * 2. UA 正则（`isMobile()`）：兼容未实现 Client Hints 的浏览器（如 Safari）。
 * 3. iPad 专用兜底：iPadOS 13+ Safari 默认把 UA 报成 "Macintosh"（与 macOS 电脑一致），
 *    用 `platform === 'MacIntel' && maxTouchPoints > 1` 识别"报成 Mac 但其实是触屏"的现代 iPad。
 * 4. 以上信号都不可用时（如 SSR/测试环境），退化为视口宽度 ≤768px 兜底。
 *
 * 注意：用户主动在浏览器里切换"电脑版"会使上述 1/2/3 均判定为非移动设备，这是用户主动选择，
 * 属预期行为，不在本函数修正范围内。
 */
export function isMobileDevice(): boolean {
  if (typeof navigator === 'undefined') {
    return false
  }

  const uaData = (navigator as Navigator & { userAgentData?: { mobile?: boolean } }).userAgentData
  if (uaData && typeof uaData.mobile === 'boolean') {
    return uaData.mobile
  }

  if (isMobile()) {
    return true
  }

  if (navigator.platform === 'MacIntel' && (navigator.maxTouchPoints ?? 0) > 1) {
    return true
  }

  if (typeof window !== 'undefined' && typeof window.matchMedia === 'function') {
    return window.matchMedia('(max-width: 768px)').matches
  }

  return false
}

/**
 * 获取文件扩展名
 * @param filename 文件名
 * @returns 扩展名
 */
export function getFileExtension(filename: string): string {
  return filename.slice((filename.lastIndexOf('.') - 1 >>> 0) + 2)
}

// formatFileSize 已统一到 @/shared/utils/formatters.ts

/**
 * 验证时间码格式
 * @param timeCode 时间码字符串
 * @returns 是否有效
 */
export function validateTimeCode(timeCode: string): boolean {
  const timeCodeRegex = /^(\d{1,2}):([0-5]?\d):([0-5]?\d)\.(\d{1,3})$/
  return timeCodeRegex.test(timeCode)
}

/**
 * 时间码字符串转秒数
 * @param timeCode 时间码字符串 (hh:mm:ss.ms)
 * @returns 秒数
 */
export function timeCodeToSeconds(timeCode: string): number {
  const parts = timeCode.split(':')
  if (parts.length !== 3) return 0
  
  const hours = parseInt(parts[0], 10)
  const minutes = parseInt(parts[1], 10)
  const secondsParts = parts[2].split('.')
  const seconds = parseInt(secondsParts[0], 10)
  const milliseconds = secondsParts[1] ? parseInt(secondsParts[1].padEnd(3, '0'), 10) : 0
  
  return hours * 3600 + minutes * 60 + seconds + milliseconds / 1000
}

/**
 * 秒数转时间码字符串
 * @param seconds 秒数
 * @returns 时间码字符串
 */
export function secondsToTimeCode(seconds: number): string {
  const hours = Math.floor(seconds / 3600)
  const minutes = Math.floor((seconds % 3600) / 60)
  const secs = Math.floor(seconds % 60)
  const milliseconds = Math.floor((seconds % 1) * 1000)
  
  return `${hours.toString().padStart(2, '0')}:${minutes.toString().padStart(2, '0')}:${secs.toString().padStart(2, '0')}.${milliseconds.toString().padStart(3, '0')}`
}